import React, { Children, cloneElement, ReactElement, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useRecordContext } from 'react-admin';
import { MC } from '../../utils/types';
import { extractIdFromURI } from '../../utils/resources/ids';
import { useDispatch } from 'react-redux';
import { saveAncsLastNoteFound } from '../../redux/actions/ancs';
import { useNotesBatch } from '../../hooks/useNotesBatch';

interface AvatarNoteReferenceFieldProps {
    label: string;
    tags: string[];
    componentIdentification: string;
    children?: ReactElement;
    headerClassName?: string;
}

const AvatarNoteReferenceField = (props: AvatarNoteReferenceFieldProps) => {
    if (React.Children.count(props.children) !== 1) {
        throw new Error('<AvatarNoteReferenceField> only accepts a single child');
    }

    const record = useRecordContext(props);
    const dispatch = useDispatch();

    // Safely compute itemIds from record.mcs if present; otherwise, use an empty array
    const itemIds = useMemo(() => {
        const mcsArray = Array.isArray((record as any)?.mcs) ? (record as any).mcs : [];
        return mcsArray
            .map((mc: MC | string) => {
                if (typeof mc === 'string') return extractIdFromURI(mc);
                if (typeof mc === 'object' && mc && 'id' in mc) return (mc as MC).id;
                return null;
            })
            .filter((id: string | null): id is string => !!id);
    }, [record]);

    // Use the batched hook instead of useGetList
    const tag = props.tags && props.tags.length > 0 ? props.tags[0] : '';
    const notesArray = useNotesBatch(tag, itemIds);
    
    // Convert array to object format expected by children components
    const data = useMemo(() => {
        if (!notesArray || notesArray.length === 0) return null;
        
        // Convert array to object with note IDs as keys
        const notesObject: any = {};
        notesArray.forEach((note: any) => {
            const noteId = note.id || note['@id'];
            if (noteId) {
                notesObject[noteId] = note;
            }
        });
        return notesObject;
    }, [notesArray]);

    useEffect(
        () => {
            if (data) {
                dispatch(
                    saveAncsLastNoteFound.fn(
                        props.componentIdentification,
                        record.id,
                        props.tags,
                        // @ts-ignore
                        data
                    )
                );
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data]
    );

    return (
        <AvatarNoteReferenceFieldView
            componentIdentification={props.componentIdentification}
            tags={props.tags}
            record={record}
            notes={data}
            headerClassName={props.headerClassName}
        >
            {props.children}
        </AvatarNoteReferenceFieldView>
    );
};

AvatarNoteReferenceField.propTypes = {
    label: PropTypes.string,
    componentIdentification: PropTypes.string,
    record: PropTypes.object,
    tags: PropTypes.arrayOf(PropTypes.string),
    children: PropTypes.element,
};

export default AvatarNoteReferenceField;

export const AvatarNoteReferenceFieldView = (props: any) => {
    return cloneElement(Children.only(props.children), {
        record: props.record,
        tags: props.tags,
        componentIdentification: props.componentIdentification,
        notes: props.notes,
        headerClassName: props.headerClassName,
    });
};

AvatarNoteReferenceFieldView.propTypes = {
    children: PropTypes.element,
    headerClassName: PropTypes.string,
    record: PropTypes.object.isRequired,
    componentIdentification: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    notes: PropTypes.object,
};
